import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const MOVE_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Move to Marche — Trasferirsi nelle Marche dall\'estero | Discovering Marche',
    description: 'Guida completa per trasferirsi nelle Marche dall\'estero: codice fiscale, permesso di soggiorno, iter notarile. Assistenza in inglese e spagnolo.',
    url: `${SITE_URL}/move-to-marche`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Move to Marche', item: `${SITE_URL}/move-to-marche` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Posso trasferirmi nelle Marche dall\'estero?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sì, è possibile trasferirsi nelle Marche dall\'estero. I cittadini UE possono farlo liberamente; i cittadini extra-UE devono ottenere il permesso di soggiorno appropriato.',
        },
      },
      {
        '@type': 'Question',
        name: 'Cosa serve per vivere nelle Marche come straniero?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sono necessari il codice fiscale, la residenza anagrafica e, per i cittadini extra-UE, il permesso di soggiorno. Discovering Marche offre assistenza completa in italiano, inglese e spagnolo.',
        },
      },
    ],
  },
];

const FAQ_DATA = [
  { q: 'move.faq.1.q', a: 'move.faq.1.a' },
  { q: 'move.faq.2.q', a: 'move.faq.2.a' },
  { q: 'move.faq.3.q', a: 'move.faq.3.a' },
  { q: 'move.faq.4.q', a: 'move.faq.4.a' },
  { q: 'move.faq.5.q', a: 'move.faq.5.a' },
];

const MoveToMarchePage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const hero = useScrollAnimation();
  const steps = useScrollAnimation({ threshold: 0.05 });
  const faq = useScrollAnimation({ threshold: 0.05 });

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Move to Marche — Trasferirsi nelle Marche dall'estero | Discovering Marche"
        description="Guida per stranieri che vogliono trasferirsi nelle Marche: codice fiscale, permesso di soggiorno, iter notarile. Assistenza in italiano, inglese e spagnolo con Discovering Marche."
        keywords="move to Marche Italy, trasferirsi Marche, expat Marche, vivere nelle Marche stranieri, borghi Marche"
        canonical="/move-to-marche"
        jsonLd={MOVE_JSONLD}
      />
      {/* Hero */}
      <section className="relative h-screen min-h-[600px] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Marche%20Italy%20beautiful%20countryside%20rolling%20hills%20golden%20sunset%20terracotta%20farmhouse%20olive%20groves%20vineyards%20dream%20life%20expat%20living%20abroad%20Italian%20landscape&width=1920&height=1080&seq=moveHero&orientation=landscape"
            alt="Move to Marche Italy — buy property Marche Italy"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 w-full flex flex-col items-center text-center px-6">
          <p
            className="font-sans text-xs tracking-widest uppercase text-terracotta-200 mb-6"
            style={{ animation: 'fadeInUp 1.2s ease-out 0.3s forwards', opacity: 0 }}
          >
            MOVE TO MARCHE
          </p>
          <h1
            className="font-serif text-5xl md:text-7xl text-white font-light leading-tight max-w-4xl mb-6"
            style={{ animation: 'fadeInUp 1.2s ease-out 0.6s forwards', opacity: 0 }}
          >
            {t('move.hero.title')}
          </h1>
          <p
            className="font-sans text-lg text-white/80 max-w-xl mb-10 leading-relaxed"
            style={{ animation: 'fadeInUp 1.2s ease-out 0.9s forwards', opacity: 0 }}
          >
            {t('move.hero.subtitle')}
          </p>
          <div style={{ animation: 'fadeInUp 1.2s ease-out 1.2s forwards', opacity: 0 }}>
            <Link
              to="/contatti"
              className="inline-block font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 rounded-full cursor-pointer whitespace-nowrap"
            >
              {t('move.hero.cta')}
            </Link>
          </div>
        </div>
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(28px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </section>

      {/* Why Marche */}
      <section
        ref={hero.ref}
        className={`fade-in-up ${hero.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">{t('move.why.overline')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6 leading-snug">
              {t('move.why.title')}
            </h2>
            <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
              {t('move.why.text')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-5">
              {[
                { value: '€ 1.200', label: t('move.stat.costo') },
                { value: '320 km', label: t('move.stat.costa') },
                { value: '< 2h', label: t('move.stat.roma') },
                { value: '3', label: t('move.stat.aeroporti') },
              ].map((stat) => (
                <div key={stat.label} className="bg-ivory-50 rounded-xl p-5 border border-ivory-200">
                  <p className="font-serif text-3xl text-terracotta-300 mb-1">{stat.value}</p>
                  <p className="font-sans text-xs text-anthracite-500 leading-snug">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
              <img
                src="https://readdy.ai/api/search-image?query=expat%20family%20Italy%20Marche%20countryside%20outdoor%20lunch%20terrace%20vineyard%20olive%20trees%20quality%20of%20life%20authentic%20Italian%20living%20dream%20home&width=800&height=450&seq=moveWhy1&orientation=landscape"
                alt="Vivere nelle Marche — move to Italy Marche"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-xl overflow-hidden aspect-square">
              <img
                src="https://readdy.ai/api/search-image?query=Italian%20market%20fresh%20produce%20Marche%20food%20wine%20local%20products%20authentic%20lifestyle%20countryside&width=400&height=400&seq=moveWhy2&orientation=squarish"
                alt="Mercato locale Marche"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-xl overflow-hidden aspect-square">
              <img
                src="https://readdy.ai/api/search-image?query=Adriatic%20sea%20beach%20Conero%20Marche%20Italy%20crystal%20clear%20water%20white%20cliffs%20beautiful%20summer&width=400&height=400&seq=moveWhy3&orientation=squarish"
                alt="Riviera del Conero"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="bg-ivory-50 py-20 px-6">
        <div
          ref={steps.ref}
          className={`fade-in-up ${steps.isVisible ? 'visible' : ''} max-w-5xl mx-auto`}
        >
          <div className="text-center mb-14">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">{t('move.process.overline')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light">{t('move.process.title')}</h2>
          </div>

          <div className="space-y-6">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex gap-6 bg-white rounded-2xl p-6 border border-ivory-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-terracotta-50 border-2 border-terracotta-300 flex-shrink-0">
                  <span className="font-serif text-lg text-terracotta-300">{step}</span>
                </div>
                <div>
                  <h3 className="font-serif text-xl text-anthracite-700 mb-2">{t(`move.step${step}.title`)}</h3>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{t(`move.step${step}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div
          ref={faq.ref}
          className={`fade-in-up ${faq.isVisible ? 'visible' : ''} max-w-3xl mx-auto`}
        >
          <div className="text-center mb-12">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">{t('move.faq.overline')}</p>
            <h2 className="font-serif text-3xl text-anthracite-800 font-light">{t('move.faq.title')}</h2>
          </div>

          <div className="space-y-3">
            {FAQ_DATA.map((item, i) => (
              <div key={i} className="border border-ivory-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-ivory-50 transition-colors"
                >
                  <span className="font-serif text-base text-anthracite-700">{t(item.q)}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-4">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-terracotta-300`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{t(item.a)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-anthracite-800 py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white font-light mb-6">{t('move.cta.title')}</h2>
          <p className="font-sans text-sm text-white/60 mb-8 leading-relaxed">{t('move.cta.text')}</p>
          <Link
            to="/contatti"
            className="inline-block font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 rounded-full cursor-pointer whitespace-nowrap"
          >
            {t('move.cta.btn')}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default MoveToMarchePage;
